<template>
  <TableLayout :permissions="['meihui:posterTemplate:query']">
    <el-form ref="searchForm" slot="search-form" :model="searchForm" label-width="80px" inline>
      <el-form-item label="名称"><el-input v-model="searchForm.name" placeholder="名称" @keypress.enter.native="search" /></el-form-item>
      <el-form-item label="场景">
        <el-select v-model="searchForm.scene" placeholder="场景" clearable>
          <el-option label="日记分享" value="diaryShare" />
          <el-option label="朋友圈分享" value="momentsShare" />
          <el-option label="知识卡片" value="knowledgeCard" />
        </el-select>
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="searchForm.type" placeholder="分类" clearable>
          <el-option label="每日一记模板" value="dailyDiary" />
          <el-option label="情绪感悟模板" value="emotion" />
          <el-option label="成长手记模板" value="growthHandnote" />
          <el-option label="简约海报模板" value="simplePoster" />
          <el-option label="治愈卡片模板" value="healingCard" />
        </el-select>
      </el-form-item>
      <el-form-item label="会员">
        <el-select v-model="searchForm.vipOnly" placeholder="会员" clearable>
          <el-option label="全体" :value="0" />
          <el-option label="仅会员" :value="1" />
        </el-select>
      </el-form-item>
      <section><el-button type="primary" @click="search">搜索</el-button><el-button @click="reset">重置</el-button></section>
    </el-form>
    <template v-slot:table-wrap>
      <ul class="toolbar">
        <li><el-button type="primary" @click="$refs.opera.open('新建模板')" icon="el-icon-plus" v-permissions="['meihui:posterTemplate:create']">新建</el-button></li>
        <li><el-button @click="deleteByIdInBatch" icon="el-icon-delete" v-permissions="['meihui:posterTemplate:delete']">批量删除</el-button></li>
      </ul>
      <el-table v-loading="isWorking.search" :data="tableData.list" stripe @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" />
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="name" label="模板名称" width="160" show-overflow-tooltip />
        
        <!-- 封面预览图 -->
        <el-table-column prop="previewUrl" label="封面预览图" width="120">
          <template slot-scope="{row}">
            <el-image
              v-if="row.previewUrl"
              style="width: 80px; height: 80px"
              :src="imgUrl + row.previewUrl"
              fit="cover"
              :preview-src-list="[imgUrl + row.previewUrl]"
            >
              <template #error>
                <div style="display: flex; justify-content: center; align-items: center; height: 100%; background: #f5f7fa;">
                  <i class="el-icon-picture-outline" style="font-size: 24px; color: #c0c4cc;"></i>
                </div>
              </template>
            </el-image>
            <span v-else style="color: #c0c4cc; font-size: 12px;">无图片</span>
          </template>
        </el-table-column>
        
        <!-- 背景图 -->
        <el-table-column prop="bgUrl" label="背景图" width="120">
          <template slot-scope="{row}">
            <el-image
              v-if="row.bgUrl"
              style="width: 80px; height: 80px"
              :src="imgUrl + row.bgUrl"
              fit="cover"
              :preview-src-list="[imgUrl + row.bgUrl]"
            >
              <template #error>
                <div style="display: flex; justify-content: center; align-items: center; height: 100%; background: #f5f7fa;">
                  <i class="el-icon-picture-outline" style="font-size: 24px; color: #c0c4cc;"></i>
                </div>
              </template>
            </el-image>
            <span v-else style="color: #c0c4cc; font-size: 12px;">无图片</span>
          </template>
        </el-table-column>

        <el-table-column prop="scene" label="场景" width="100">
          <template slot-scope="{row}">
            <el-tag size="small">{{ sceneLabel(row.scene) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="分类" width="120">
          <template slot-scope="{row}">
            <el-tag size="small" :type="typeColor(row.type)">{{ typeLabel(row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="difyPrompt" label="Dify生成提示词" min-width="220" show-overflow-tooltip>
          <template slot-scope="{row}">
            <span v-if="row.difyPrompt">{{ row.difyPrompt }}</span>
            <span v-else style="color: #c0c4cc;">未配置</span>
          </template>
        </el-table-column>
        <el-table-column prop="canvasWidth" label="画布尺寸" width="100">
          <template slot-scope="{row}">{{ row.canvasWidth }}×{{ row.canvasHeight }}</template>
        </el-table-column>
        <el-table-column prop="vipOnly" label="会员" width="80">
          <template slot-scope="{row}">
            <el-tag size="small" :type="row.vipOnly ? 'warning' : 'info'">{{ row.vipOnly ? '仅会员' : '全体' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="60" />
        <el-table-column prop="isEnabled" label="启用" width="70">
          <template slot-scope="{row}">
            <el-switch :value="!!row.isEnabled" disabled />
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160" />
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{row}">
            <el-button type="text" @click="$refs.opera.open('编辑模板', row)" icon="el-icon-edit" v-permissions="['meihui:posterTemplate:update']">编辑</el-button>
            <el-button type="text" @click="deleteById(row)" icon="el-icon-delete" v-permissions="['meihui:posterTemplate:delete']">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <pagination @size-change="handleSizeChange" @current-change="handlePageChange" :pagination="tableData.pagination" />
    </template>
    <operaposterTemplateWindow ref="opera" @success="handlePageChange" width="80%" />
  </TableLayout>
</template>
<script>
import BaseTable from '@/components/base/BaseTable'
import TableLayout from '@/layouts/TableLayout'
import Pagination from '@/components/common/Pagination'
import operaposterTemplateWindow from './operaposterTemplateWindow'

const SCENE_MAP = { diaryShare: '日记分享', momentsShare: '朋友圈分享', knowledgeCard: '知识卡片' }
const TYPE_MAP = { dailyDiary: '每日一记模板', emotion: '情绪感悟模板', growthHandnote: '成长手记模板', simplePoster: '简约海报模板', healingCard: '治愈卡片模板' }

export default {
  name: 'PosterTemplate', extends: BaseTable, components: { TableLayout, Pagination, operaposterTemplateWindow },
  data() { 
    return { 
      searchForm: {},
      imgUrl: process.env.VUE_APP_API_IMG
    } 
  },
  created() {
    this.config({ module: '海报模板', api: '/rentHouse/posterTemplate', 'field.id': 'id', 'field.main': 'name' })
    this.search()
  },
  methods: {
    sceneLabel(v) { return SCENE_MAP[v] || v },
    typeLabel(v) { return TYPE_MAP[v] || v },
    typeColor(v) {
      const map = { dailyDiary: '', emotion: 'danger', growthHandnote: 'success', simplePoster: 'info', healingCard: 'warning' }
      return map[v] || ''
    }
  }
}
</script>
